import React from "react";
import { X, Warning } from "@phosphor-icons/react";
import {
    isImpersonating,
    getImpersonationTargetName,
    exitImpersonation,
} from "@/lib/impersonation";

/**
 * iter101.47 — Compact impersonation banner (same-tab flow).
 *
 * Replaces the previous sticky top strip. Now a small amber chip
 * anchored top-right of the viewport with a single X icon to exit,
 * matching `ImpersonationSessionPill` (the new-tab counterpart) so
 * both impersonation flows read as one universal grammar across every
 * portal (Doctor, Patient, CoE, Admin).
 *
 * Renders nothing when there's no impersonation backup in localStorage,
 * so it's safe to mount once at the App root.
 */
export function ImpersonationBanner() {
    if (!isImpersonating()) return null;
    const target = getImpersonationTargetName();
    return (
        <div
            data-testid="impersonation-banner"
            className="fixed top-3 left-1/2 -translate-x-1/2 sm:top-4 z-[10000] pointer-events-none"
        >
            <div
                className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50/95 shadow-md shadow-amber-200/40 backdrop-blur px-2 py-1 text-[11px] max-w-[min(90vw,320px)]"
                title={`Impersonating ${target || "user"} — click X to return to admin`}
            >
                <Warning size={11} weight="fill" className="text-amber-700 shrink-0" />
                <span className="text-[9px] uppercase tracking-widest font-bold text-amber-700 shrink-0 hidden sm:inline">
                    Impersonating
                </span>
                <span className="text-amber-900 font-semibold truncate">
                    {target || "user"}
                </span>
                <button
                    type="button"
                    onClick={exitImpersonation}
                    data-testid="exit-impersonation-btn"
                    className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-700 hover:bg-amber-800 text-white transition-colors shrink-0"
                    aria-label="Exit impersonation"
                    title="Exit impersonation"
                >
                    <X size={10} weight="bold" />
                </button>
            </div>
        </div>
    );
}

export default ImpersonationBanner;
